import { createApi } from '@reduxjs/toolkit/query/react';

import type {
  ClaimRewardResponseSchemaType,
  GetClaimRewardResponseSchemaType,
  GetRewardStatsResponseSchemaType,
  GetRewardsResponseSchemaType,
  RewardRequestSchemaType,
  RewardStatsSchemaType,
} from '@/global/schemas/reward.schema';

import { REDUX_SLICE } from '../constants';
import { axiosBaseQuery } from '../utils';

export const rewardApi = createApi({
  reducerPath: REDUX_SLICE.REWARD_API,
  tagTypes: ['ClaimRewardV1', 'ClaimRewardV2', 'RewardStatsV1', 'RewardStatsV2', 'RewardsV1', 'RewardsV2'],
  baseQuery: axiosBaseQuery,
  endpoints: builder => ({
    fetchRewards: builder.query<
      GetRewardsResponseSchemaType,
      RewardRequestSchemaType & { page?: number; size?: number; nodeVersion: 'v1' | 'v2' }
    >({
      query: ({ address, nft, page = 1, size = 10, nodeVersion }) => {
        if (nodeVersion === 'v1') {
          return {
            path: `v1/projects/${nft}/wallets/${address}/operator-histories`,
            version: 'v1',
            params: { page, size },
          };
        }

        return {
          path: `v1/rewards`,
          params: { page, size },
          headers: {
            'x-offchain-address': address,
          },
        };
      },
      transformResponse: (returnValue: GetRewardsResponseSchemaType) => returnValue,
      providesTags: (_result, _error, { address, nft }) => [{ type: 'RewardsV1', id: `reward_lst_${address}_${nft}` }],
    }),
    fetchRewardStats: builder.query<RewardStatsSchemaType, RewardRequestSchemaType & { page: string }>({
      query: ({ address, nft }) => ({
        path: `v1/projects/${nft}/wallets/${address}/operators`,
        version: 'v1',
      }),
      transformResponse: (returnValue: GetRewardStatsResponseSchemaType) => returnValue.data,
      providesTags: (_result, _error, { address, nft, page }) => [
        { type: 'RewardStatsV1', id: `reward_stats_${page}_${address}_${nft}` },
      ],
    }),
    fetchRewardStatsV2: builder.query<RewardStatsSchemaType, RewardRequestSchemaType & { page: string }>({
      query: ({ address, nft }) => ({
        path: `v1/projects/${nft}/rewards`,
        headers: {
          'x-offchain-address': address,
        },
      }),
      transformResponse: (returnValue: GetRewardStatsResponseSchemaType) => returnValue.data,
      providesTags: (_result, _error, { address, nft, page }) => [
        { type: 'RewardStatsV1', id: `reward_stats_${page}_${address}_${nft}` },
      ],
    }),
    claimRewardV1: builder.mutation<ClaimRewardResponseSchemaType, RewardRequestSchemaType>({
      query: data => ({
        method: 'POST',
        path: `v1/projects/${data.nft}/wallets/${data.address}/operators`,
        data: null,
        version: 'v1',
      }),
      transformResponse: (returnValue: GetClaimRewardResponseSchemaType) => returnValue.data,
      invalidatesTags: (_result, _error) => [{ type: 'ClaimRewardV1' }],
    }),
    claimRewardV2: builder.mutation<ClaimRewardResponseSchemaType, RewardRequestSchemaType>({
      query: ({ address, nft }) => ({
        method: 'POST',
        path: `v1/projects/${nft}/rewards`,
        headers: {
          'x-offchain-address': address,
        },
      }),
      transformResponse: (returnValue: GetClaimRewardResponseSchemaType) => returnValue.data,
      invalidatesTags: (_result, _error) => [{ type: 'ClaimRewardV2' }],
    }),
  }),
});

export const {
  useClaimRewardV1Mutation,
  useFetchRewardsQuery,
  useFetchRewardStatsQuery,
  useFetchRewardStatsV2Query,
  useClaimRewardV2Mutation,
} = rewardApi;
